import {sourceMediaRequest} from '@waka/compat/media-request';
import { BrowserRenderMediaAudio as MediaAudio } from '@waka/compat/browser-render-media';
import React, { createContext, useContext, useMemo } from 'react';
import {
  Html5Audio,
  Sequence,
  useCurrentFrame,
  useRemotionEnvironment,
  useVideoConfig,
} from 'remotion';
import type { EditorStarterAsset } from '../assets/assets';
import { RequireCachedAsset } from '../caching/require-cached-asset';
import { decibelToGain } from '../utils/decibels';
import { useStablePreferredLocalUrl } from '../utils/find-asset-by-id';
import { useAllItems, useAssets } from '../utils/use-context';
import type { ContinuousAudioGroup } from './continuous-audio-groups';
import { GlobalTtsItemIdsProvider } from './global-tts-audio-layer';
import {
  getPlaybackBoundaryWindowsForItems,
  isPlaybackBoundaryMediaPauseDisabledForWindows,
  type PlaybackBoundaryWindow,
} from './utils/playback-boundary-premount';

interface RenderableContinuousAudioGroup extends ContinuousAudioGroup {
  asset: EditorStarterAsset;
  boundaryWindows: PlaybackBoundaryWindow[];
}

const SingleContinuousAudio: React.FC<{
  group: RenderableContinuousAudioGroup;
}> = ({ group }) => {
  const { fps } = useVideoConfig();
  const currentFrame = useCurrentFrame();
  const { isClientSideRendering, isRendering } = useRemotionEnvironment();
  const src = useStablePreferredLocalUrl(group.asset);
  const durationInFrames = group.toFrame - group.fromFrame;
  const useRendererAudio =
    isClientSideRendering || (isRendering && group.playbackRate >= 1);
  const disableBoundaryMediaPause =
    isPlaybackBoundaryMediaPauseDisabledForWindows({
      currentFrame,
      windows: group.boundaryWindows,
    });
  const volume = useMemo(() => {
    const gain = decibelToGain(group.decibelAdjustment);
    return () => gain;
  }, [group.decibelAdjustment]);

  return (
    <RequireCachedAsset asset={group.asset}>
      <Sequence
        from={group.fromFrame}
        durationInFrames={durationInFrames}
        premountFor={Math.floor(3 * fps)}
        style={{ display: 'contents' }}
      >
        {useRendererAudio ? (
          <MediaAudio
            requestInit={sourceMediaRequest}
            trimBefore={group.trimBeforeFrames}
            src={src}
            volume={volume}
            playbackRate={group.playbackRate}
            muted={group.trackMuted}
          />
        ) : (
          <Html5Audio
            crossOrigin="anonymous"
            pauseWhenBuffering={!disableBoundaryMediaPause}
            trimBefore={group.trimBeforeFrames}
            src={src}
            useWebAudioApi
            volume={volume}
            playbackRate={group.playbackRate}
            muted={group.trackMuted}
          />
        )}
      </Sequence>
    </RequireCachedAsset>
  );
};

export const GlobalContinuousAudioLayer: React.FC<{
  groups: ContinuousAudioGroup[];
  boundaryPremountFramesByItemId?: ReadonlyMap<string, number>;
}> = ({ groups, boundaryPremountFramesByItemId }) => {
  const { items } = useAllItems();
  const { assets } = useAssets();

  const renderableGroups = useMemo(
    () =>
      groups.flatMap((group): RenderableContinuousAudioGroup[] => {
        const asset = assets[group.assetId];
        if (!asset) return [];

        return [
          {
            ...group,
            asset,
            boundaryWindows: getPlaybackBoundaryWindowsForItems({
              items: group.itemIds.flatMap((itemId) => {
                const item = items[itemId];
                return item ? [item] : [];
              }),
              boundaryPremountFramesByItemId,
            }),
          },
        ];
      }),
    [assets, boundaryPremountFramesByItemId, groups, items],
  );

  return (
    <>
      {renderableGroups.map((group) => (
        <SingleContinuousAudio
          key={`${group.trackId}-${group.assetId}-${group.fromFrame}`}
          group={group}
        />
      ))}
    </>
  );
};

const GlobalContinuousAudioItemIdsContext = createContext<Set<string>>(
  new Set(),
);

export const GlobalContinuousAudioItemIdsProvider: React.FC<{
  value: Set<string>;
  children: React.ReactNode;
}> = ({ value, children }) => (
  <GlobalContinuousAudioItemIdsContext.Provider value={value}>
    {children}
  </GlobalContinuousAudioItemIdsContext.Provider>
);

export const GlobalAudioItemIdsProvider: React.FC<{
  ttsItemIds: Set<string>;
  continuousItemIds: Set<string>;
  children: React.ReactNode;
}> = ({ ttsItemIds, continuousItemIds, children }) => (
  <GlobalTtsItemIdsProvider value={ttsItemIds}>
    <GlobalContinuousAudioItemIdsProvider value={continuousItemIds}>
      {children}
    </GlobalContinuousAudioItemIdsProvider>
  </GlobalTtsItemIdsProvider>
);

export const useIsGlobalContinuousAudioItem = (itemId: string): boolean => {
  const itemIds = useContext(GlobalContinuousAudioItemIdsContext);
  return itemIds.has(itemId);
};
